import { auth } from '../firebase';

// Define the message type for chat completions
export interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

// Define the response type
export interface ChatCompletionResponse {
  success: boolean;
  data?: {
    role: string;
    content: string;
  };
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
  error?: string;
  message?: string;
}

/**
 * Enhances messages with a system instruction about todo formatting
 * @param messages User messages to be enhanced
 * @returns Enhanced messages with todo formatting instruction
 */
export const enhanceMessagesWithTodoFormat = (messages: ChatMessage[]): ChatMessage[] => {
  const todoFormatInstruction: ChatMessage = {
    role: 'system',
    content: 'When listing todos or action items, use square brackets like "[] Task to do" at the beginning of each item. This helps the user track tasks.'
  };
  
  // Check if there's already a system message
  const hasSystemMessage = messages.some(msg => msg.role === 'system');
  
  if (hasSystemMessage) {
    // If there's a system message, add our instruction to it
    return messages.map(msg => {
      if (msg.role === 'system') {
        return {
          ...msg,
          content: `${msg.content}\n\nWhen listing todos or action items, use square brackets like "[] Task to do" at the beginning of each item. When asked for todos, avoid numbers of sub headings, just give a simple list of todos `
        }; 
      }
      return msg;
    });
  } else {
    // If no system message, add ours at the beginning
    return [todoFormatInstruction, ...messages];
  }
};

/**
 * Generate a chat completion using OpenAI API
 * @param messages Array of messages for the conversation
 * @param model OpenAI model to use (defaults to gpt-3.5-turbo)
 * @param maxTokens Maximum tokens for the response
 * @returns Chat completion response
 */
export const generateChatCompletion = async (
  messages: ChatMessage[],
  model: string = 'gpt-4o',
  maxTokens: number = 200
): Promise<ChatCompletionResponse> => {
  try {
    // Get the current user's ID token for authentication
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    const idToken = await currentUser.getIdToken();
    
    // Define the API endpoint URL
    const apiUrl = 'https://us-central1-vybecode-36007.cloudfunctions.net/api/openai/chat';
    
    // Enhance messages with todo formatting instruction
    const enhancedMessages = enhanceMessagesWithTodoFormat(messages);
    
    // Make the request to the backend
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify({
        messages: enhancedMessages,
        model,
        maxTokens
      })
    });
    
    // Parse the response
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to generate chat completion');
    }
    
    return data;
  } catch (error: any) {
    console.error('Error generating chat completion:', error);
    return {
      success: false,
      error: 'Error',
      message: error.message || 'Something went wrong'
    };
  }
}; 